import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { LetDirective } from '@ngrx/component';
import { ViLoaderComponent } from '@vi-iot/ui';
import { isEqual } from 'lodash-es';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  AppServiceType,
  AssistantOverlayTypeEnum,
  SelectedDocumentsState,
} from 'src/app/@store/assistant/assistant.model';
import { AssistantDocumentType, AssistantStore } from '../../../../@store/assistant';
import { AssistantHelperService } from '../../../../@store/assistant/assistant.helper.service';
import { CommonStandaloneModule } from '../../../../shared/common-standalone.module';
import { PdfPreviewComponent } from '../../../../shared/components/pdf-preview/pdf-preview.component';
import { StripHtmlPipe } from '../../../../shared/pipes/strip-html/strip-html.pipe';
import { ViassistAllowedHtmlPipe } from '../../../pipes/viassist-allowed-html/viassist-allowed-html.pipe';
import { AssistantOverlayActionButtonsComponent } from '../../assistant-overlay-action-buttons/assistant-overlay-action-buttons.component';
import { BannerComponent } from '../../../../shared/components/banner/banner.component';

@Component({
  standalone: true,
  selector: 'app-assistant-document-detail-view',
  templateUrl: './assistant-document-detail-view.component.html',
  styleUrls: ['./assistant-document-detail-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonStandaloneModule,
    StripHtmlPipe,
    MatButtonModule,
    MatIconModule,
    PdfPreviewComponent,
    ViassistAllowedHtmlPipe,
    ViLoaderComponent,
    LetDirective,
    AssistantOverlayActionButtonsComponent,
    BannerComponent,
  ],
})
export class AssistantDocumentDetailViewComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() displayActionButtons = false;
  @Output() pageChanged = new EventEmitter<void>();
  processedHtml: string;

  protected readonly assistantDocumentType = AssistantDocumentType;

  public AssistantOverlayTypeEnum = AssistantOverlayTypeEnum;
  public documentType: AssistantDocumentType;
  public dataLoading = true;
  public documentWithError = false;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public assistantStore: AssistantStore,
    public assistantHelperService: AssistantHelperService
  ) {}

  ngOnInit(): void {
    this.assistantStore.selectedDocumentState$
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedDocumentState: SelectedDocumentsState) => {
        if (selectedDocumentState.content.error) {
          this.dataLoading = false;
          this.documentWithError = true;

          return;
        }

        this.dataLoading =
          !selectedDocumentState.content.loaded ||
          selectedDocumentState.images.loading ||
          selectedDocumentState.details.loading;
        this.documentWithError = false;

        const html = selectedDocumentState.content.value;
        this.processedHtml = this.cleanNbspParagraphs(html);

        if (!selectedDocumentState.content.value && !selectedDocumentState.content.loading) {
          if (selectedDocumentState.searchDocument) {
            this.assistantStore.getDocumentContent(AppServiceType.ViGuide);
          } else if (
            selectedDocumentState.details.value &&
            !selectedDocumentState.details.loading
          ) {
            this.assistantStore.getDocumentContentById(
              selectedDocumentState.details.value.id,
              AppServiceType.ViGuide
            );
          }
        }
      });
  }

  ngAfterViewChecked(): void {
    if (this.processedHtml) {
      setTimeout(() => this.addWatermarkToImagesInDOM(), 0);
    }
  }

  private cleanNbspParagraphs(html: string): string {
    if (!html) {
      return '';
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const paragraphs = Array.from(doc.querySelectorAll('p'));
    let consecutiveEmpty = 0;
    for (const p of paragraphs) {
      const isOnlyNbsp = p.innerHTML.replace(/&nbsp;/g, '').trim() === '';
      if (isOnlyNbsp) {
        consecutiveEmpty++;
        if (consecutiveEmpty > 1) {
          p.remove();
        }
      } else {
        consecutiveEmpty = 0;
      }
    }

    return doc.body.innerHTML;
  }

  private isImageLargeEnough(img: HTMLImageElement): boolean {
    const widthAttr = parseInt(img.getAttribute('width') || '0', 10);
    const heightAttr = parseInt(img.getAttribute('height') || '0', 10);
    if ((widthAttr && widthAttr < 100) || (heightAttr && heightAttr < 100)) {
      return false;
    }

    return img.naturalWidth >= 100 && img.naturalHeight >= 100;
  }

  private wrapImageWithWatermark(img: HTMLImageElement): void {
    if (img.parentElement?.classList.contains('watermarked-image')) {
      return;
    }
    const wrapper = document.createElement('div');
    wrapper.className = 'watermarked-image';
    img.parentNode?.insertBefore(wrapper, img);
    wrapper.appendChild(img);
    const watermark = document.createElement('div');
    watermark.className = 'watermark';
    watermark.textContent = '© Viessmann Climate Solutions SE';
    wrapper.appendChild(watermark);
    img.setAttribute('data-watermarked', 'true');
  }

  private addWatermarkToImagesInDOM(): void {
    const imgs = document.querySelectorAll('.document-wrapper img:not([data-watermarked])');
    imgs.forEach((img: HTMLImageElement) => {
      const handle = (): void => {
        if (this.isImageLargeEnough(img)) {
          this.wrapImageWithWatermark(img);
        }
      };
      if (img.complete) {
        handle();
      } else {
        img.onload = handle;
      }
    });
  }

  onPageChanged(): void {
    this.pageChanged.emit();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
